import { useEffect } from 'react';
import type { DependencyList, RefObject } from 'react';

import { normalizeTargets } from '../utils';

//  Based on https://github.com/microsoft/TypeScript/issues/33047#issuecomment-704005614
type EventMapForTarget<Target extends EventTarget> = Target extends HTMLElement
  ? HTMLElementEventMap
  : Target extends SVGElement
    ? SVGElementEventMap
    : Target extends Document
      ? DocumentEventMap
      : Target extends Window
        ? WindowEventMap
        : {
            [eventType: string]: Event;
          };
type EventNamesForTarget<T extends EventTarget> = keyof EventMapForTarget<T> & string;

/**
 * @example useEvent('keydown', (event) => { do something });
 * @param cb The function that should be executed on keydown keyevent.
 * @param options
 */
function useEvent<Target extends Document, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  opts?: {
    target?: undefined;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies: DependencyList;
  }
): void;

function useEvent<Target extends EventTarget, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  opts: {
    target: Target | RefObject<Target> | null;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  }
): void;

function useEvent<
  Target extends EventTarget,
  EventName extends EventNamesForTarget<Target | Document>
>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target | Document>[EventName]) => void,
  opts: {
    target?: Target | RefObject<Target> | null | undefined;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  }
): void;

function useEvent(
  eventName: string,
  cb: (e: Event) => void,
  opts?: {
    target?: EventTarget | RefObject<EventTarget> | null | undefined;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  }
): void;

function useEvent<Target extends EventTarget, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  {
    target = document as unknown as Target,
    eventOptions,
    dependencies = []
  }: {
    target?: Target | RefObject<Target> | null;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  } = {}
) {
  useEffect(() => {
    const [t] = normalizeTargets([target]);
    if (!t) return;

    t.addEventListener(eventName, cb as EventListener, eventOptions);

    return () => {
      t.removeEventListener(eventName, cb as EventListener, eventOptions);
    };
  }, [eventName, target, cb, eventOptions, ...dependencies]);
}

export default useEvent;
